import {
  FilesetResolver,
  FaceLandmarker,
  FaceLandmarkerResult,
} from "@mediapipe/tasks-vision";

// Pinned to the exact version in package.json ("@mediapipe/tasks-vision":
// "^0.10.32"). This MUST stay in sync with that dependency — using
// "@latest" here let jsDelivr silently swap in a newer WASM build than the
// JS API this SDK was built/tested against, breaking face detection for
// every user at once with no code change on our side. Bump both together.
const MEDIAPIPE_VERSION = "0.10.32";
const WASM_CDN =
  `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`;
// Also pinned (was "/latest/") so a model update can't shift blendshape
// output out from under ActionDetector's calibrated thresholds unannounced.
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

// Module-level cache — survives component remounts and retries within the same page session.
// First init fetches ~8MB total (WASM + model); subsequent inits reuse the cache instantly.
// Cached as in-flight PROMISES (not resolved values) so concurrent init() calls
// (e.g. parallel calls from two liveness attempts, or a fast retry) share one
// fetch instead of racing duplicate downloads — and so a failed attempt
// (rejected promise) doesn't get treated as "cached" and reused forever.
let _visionPromise: ReturnType<typeof FilesetResolver.forVisionTasks> | null = null;
let _modelBufferPromise: Promise<ArrayBuffer> | null = null;

/** Max time to wait for the WASM runtime / model download before giving up. */
const INIT_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out. Please check your connection and try again.`)),
      ms
    );
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}

export interface FaceDetectionResult {
  faceDetected: boolean;
  blendshapes: Map<string, number>;
  transformMatrix: number[] | null;
}

export class FaceDetectorEngine {
  private landmarker: FaceLandmarker | null = null;

  async init(): Promise<void> {
    // Reuse (or join) the in-flight WASM fileset load — avoids re-fetching
    // ~3MB on retry/remount, and avoids kicking off a second parallel fetch
    // if init() is called again before the first finishes.
    if (!_visionPromise) {
      _visionPromise = FilesetResolver.forVisionTasks(WASM_CDN);
      _visionPromise.catch(() => { _visionPromise = null; }); // don't cache a failure
    }

    // Reuse (or join) the in-flight model download — same reasoning.
    if (!_modelBufferPromise) {
      _modelBufferPromise = fetch(MODEL_URL).then((res) => {
        if (!res.ok) throw new Error(`Failed to download face model (${res.status})`);
        return res.arrayBuffer();
      });
      _modelBufferPromise.catch(() => { _modelBufferPromise = null; }); // don't cache a failure
    }

    const [vision, modelBuffer] = await withTimeout(
      Promise.all([_visionPromise, _modelBufferPromise]),
      INIT_TIMEOUT_MS,
      "Face detection setup"
    );

    this.landmarker = await withTimeout(
      FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          // Pass a copy — MediaPipe takes ownership of the buffer
          modelAssetBuffer: new Uint8Array(modelBuffer.slice(0)),
        },
        runningMode: "VIDEO",
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        numFaces: 1,
      }),
      INIT_TIMEOUT_MS,
      "Face detection setup"
    );
  }

  detect(video: HTMLVideoElement, timestamp: number): FaceDetectionResult {
    if (!this.landmarker) {
      return { faceDetected: false, blendshapes: new Map(), transformMatrix: null };
    }

    let result: FaceLandmarkerResult;
    try {
      result = this.landmarker.detectForVideo(video, timestamp);
    } catch {
      // Video may not be ready yet — return no-face gracefully
      return { faceDetected: false, blendshapes: new Map(), transformMatrix: null };
    }

    if (!result.faceBlendshapes || result.faceBlendshapes.length === 0) {
      return { faceDetected: false, blendshapes: new Map(), transformMatrix: null };
    }

    const blendshapes = new Map<string, number>();
    for (const bs of result.faceBlendshapes[0].categories) {
      blendshapes.set(bs.categoryName, bs.score);
    }

    const transformMatrix =
      result.facialTransformationMatrixes &&
      result.facialTransformationMatrixes.length > 0
        ? (Array.from(result.facialTransformationMatrixes[0].data) as number[])
        : null;

    return { faceDetected: true, blendshapes, transformMatrix };
  }

  destroy(): void {
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }
}
